import * as DeviceResource from '@jarvis/react-device-resources-provider'
import {
  DeviceProvider,
  DeviceProviderError,
  DeviceResourceResult,
  ExecutionStatus,
  ResourceProvider
} from '@jarvis/react-device-resources-provider'
import { Logger } from '../../utils/helperMethods'
import { ErrorCode } from '../bootstrap-events'
import {
  ResourceError,
  ExecutionError
} from '../../interface/controllerInterface'

export class BootstrapResourceDevice {
  //
  public getResourceFromFile = async (
    sessionId: string,
    predicateName: string,
    recipeApplicationName: string,
    isDebugMode: boolean
  ): Promise<DeviceResource.DeviceResource[] | ResourceError> => {
    //
    const deviceProvider: DeviceProvider = {
      printerSessionId: sessionId,
      predicateName: predicateName,
      recipeApplicationName: recipeApplicationName
    }
    return this.getResources(deviceProvider, isDebugMode)
  }

  public getResourceFromRecipe = async (
    sessionId: string,
    predicateName: string,
    recipeData: string,
    isDebugMode: boolean
  ): Promise<DeviceResource.DeviceResource[] | ResourceError> => {
    //
    const deviceProvider: DeviceProvider = {
      printerSessionId: sessionId,
      predicateName: predicateName,
      recipeData: recipeData
    }
    return this.getResources(deviceProvider, isDebugMode)
  }

  public getResources = async (
    configuration: DeviceProvider,
    isDebugMode: boolean
  ): Promise<DeviceResource.DeviceResource[] | ResourceError> => {
    //
    Logger.strLog(
      'BootstrapResourceDevice.getResource. Parameters on DeviceProvider: ',
      configuration
    )

    const resourceProvider: ResourceProvider = new ResourceProvider(isDebugMode)

    const recipeResult:
      | DeviceResourceResult[]
      | DeviceProviderError = await resourceProvider.getResource(configuration)

    if (this.hasDeviceProviderError(recipeResult)) {
      const deviceProviderError: DeviceProviderError = recipeResult
      Logger.strError(
        'BootstrapResourceDevice.getResource. DeviceProviderError: ',
        deviceProviderError
      )
      return { errorCode: ErrorCode.EBS00P0004 }
    }

    const resourceResult: DeviceResourceResult[] = recipeResult

    Logger.strError(
      'BootstrapResourceDevice.getResource. ResourceResult:',
      resourceResult
    )

    const deviceResource: DeviceResource.DeviceResource[] = []

    for (const resourceResultItem of resourceResult) {
      if (resourceResultItem.executionStatus === ExecutionStatus.Success) {
        deviceResource.push(resourceResultItem.deviceResource)
      }
    }

    if (deviceResource.length !== 0) {
      return deviceResource
    }

    // If no Success resource is returned, check if it falls on specific cases
    const errorResults = resourceResult.filter(
      (resource) => resource.responseCode == '0'
    )

    // Not specific errors, should return generic empty resources error
    if (errorResults.length === 0) {
      return { errorCode: ErrorCode.EBS00P0004 }
    }

    const listExecutionErrors: ExecutionError[] = errorResults.map(
      (errorResult) => {
        switch (errorResult.executionError) {
          case 'dataValveError':
            return {
              gun: errorResult.deviceResource.serviceMetadata.serviceGun,
              errorDetail: ErrorCode.EBS00P0007
            }

          case 'deviceOffline':
            return {
              gun: errorResult.deviceResource.serviceMetadata.serviceGun,
              errorDetail: ErrorCode.EBS00P0010
            }

          case 'requestTimedOut':
            return {
              gun: errorResult.deviceResource.serviceMetadata.serviceGun,
              errorDetail: ErrorCode.EBS00P0011
            }

          case 'invalidSession':
            return {
              gun: errorResult.deviceResource.serviceMetadata.serviceGun,
              errorDetail: ErrorCode.EBS00P0012
            }

          case 'unknownError':
            return {
              gun: errorResult.deviceResource.serviceMetadata.serviceGun,
              errorDetail: ErrorCode.EBS00P0013
            }

          case 'bodyOrDataInvalid':
            return {
              gun: errorResult.deviceResource.serviceMetadata.serviceGun,
              errorDetail: ErrorCode.EBS00P0014
            }
        }
      }
    )

    const isAllElementsEqual = !listExecutionErrors.some(
      (error) => error.errorDetail !== listExecutionErrors[0].errorDetail
    )

    if (isAllElementsEqual) {
      return { errorCode: listExecutionErrors[0].errorDetail }
    } else {
      const executionErrorCodes = listExecutionErrors.map(
        (error) => error.errorDetail
      ) as ErrorCode[]

      return {
        errorCode: this.appendExecutionErrors(
          ErrorCode.EBS00P0004,
          ...executionErrorCodes
        ),
        executionErrors: [...new Set(listExecutionErrors)]
      }
    }
  }

  private hasDeviceProviderError(
    result: DeviceResourceResult[] | DeviceProviderError
  ): result is DeviceProviderError {
    return 'providerErrorType' in result || 'providerError' in result
  }

  private appendExecutionErrors(
    mainError: ErrorCode,
    ...errors: ErrorCode[]
  ): string {
    if (errors.length === 0) return mainError

    const errorsDigits = errors.map((error) => error.slice(-2))
    return `${mainError}_${errorsDigits.join('_')}`
  }
}
